import { maskDecimal } from '@givebutter/util';
import { faEnvelope, faPhone, faReceipt } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ContactOrCompanyLabel } from 'assets/js/pages/contacts/ContactOrCompanyLabel';
import * as LinkedResourcePopover from 'components/tasks/LinkedResourcePopover';
import { CurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { isoDateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';
import { LinkableTransaction } from 'types/domain';

const TransactionResourcePopover = ({ transaction }: { transaction?: LinkableTransaction }) => {
    const { accountId, timezone } = useCurrentAccount();
    if (!transaction) return null;

    return (
        <LinkedResourcePopover.Root>
            <LinkedResourcePopover.Header>
                <FontAwesomeIcon icon={faReceipt} className="text-gray-500" />
                <span>
                    <CurrencySymbol />
                    {maskDecimal(transaction.amount)}
                </span>
                <LinkedResourcePopover.Link
                    to={`/accounts/${accountId}/transactions/${transaction.id}`}
                >
                    #{transaction.number}
                </LinkedResourcePopover.Link>
            </LinkedResourcePopover.Header>

            {transaction.contact && (
                <LinkedResourcePopover.Content>
                    {transaction.transacted_at && (
                        <div className="flex items-center justify-between gap-10">
                            <div className="flex flex-col">
                                <p className="text-sm text-gray-500">Transaction date</p>
                                <p className="text-base text-gray-900">
                                    {isoDateFromServer(transaction.transacted_at)
                                        .setZone(timezone)
                                        .toLocaleString(DateTime.DATE_FULL)}
                                </p>
                            </div>
                        </div>
                    )}
                    <div className="flex items-center justify-between gap-10">
                        <div className="flex flex-col">
                            <p className="text-sm text-gray-500">Contact</p>
                            <p className="flex items-center gap-1 text-base text-gray-900">
                                <ContactOrCompanyLabel contact={transaction.contact} />
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            {transaction.contact.primary_email && (
                                <a
                                    href={`mailto:${transaction.contact.primary_email}`}
                                    className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gray-100"
                                >
                                    <FontAwesomeIcon icon={faEnvelope} className="text-blue-500" />
                                </a>
                            )}

                            {transaction.contact.primary_phone && (
                                <a
                                    href={`tel:${transaction.contact.primary_phone}`}
                                    className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gray-100"
                                >
                                    <FontAwesomeIcon icon={faPhone} className="text-blue-500" />
                                </a>
                            )}
                        </div>
                    </div>
                </LinkedResourcePopover.Content>
            )}
        </LinkedResourcePopover.Root>
    );
};

export { TransactionResourcePopover };
